import { Controller } from "@hotwired/stimulus"

// Puts keyboard focus back in the conversation list when the write the reader just performed takes
// away the control they performed it with — which is what the spam toggle does: the thread leaves
// the list being viewed, the pane empties, and the button they just pressed no longer exists.
// Without this, focus falls to <body> and a keyboard user is dumped at the top of the document.
//
// Only acts when focus was actually lost, so a render that preserved it is left alone. Scroll is
// explicitly not moved: the same refresh signal fires when new mail arrives in the background, and
// yanking a reader's scroll position for that would be worse than the problem this solves.
export default class extends Controller {
  // Which form the reader just submitted. Remembered because it is what separates the renders this
  // controller must answer for from the ones it must ignore: a state action's response takes away
  // the control that asked for it (see conversations/list_change), while a teammate's write or new
  // mail arrives unprompted and must never pull focus out of what is being read.
  remember(event) {
    this.submittedForm = event.target
  }

  // A page refresh's morph is one way the pressed control disappears; a state action's streams are
  // the other. Rendering is wrapped rather than followed by a later event because Turbo dispatches
  // nothing after a stream renders, and the row's exit animation defers it further still (see
  // list_controller).
  rescueAfterStream(event) {
    const render = event.detail.render

    event.detail.render = async (stream) => {
      await render(stream)
      if (!this.submittedForm || this.submittedForm.isConnected) return

      this.submittedForm = null
      this.restore()
    }
  }

  restore() {
    if (document.activeElement !== document.body) return

    // Never a row that is on its way out: its stream has been rendered already, and it is only
    // still here because it is collapsing. Falls back to the list region itself (tabindex="-1" in
    // the shell) when that leaves nothing.
    const target = this.element.querySelector(".conversation-row:not([data-leaving]) .conversation-row__link") ||
                   this.element.querySelector(".app__list")
    target?.focus({ preventScroll: true })
  }
}
